import { type MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as RadixToast from '@radix-ui/react-toast';
import classNames from 'classnames';

import CloseLine from '@/assets/icons/close_line.svg?component';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';

import { closeToastEntry, type ToastEntry } from './toastStore';

import styles from './Toast.module.scss';

/** One toast in the viewport: icon, message, close button and, while it counts down, a bar. */
export function ToastItem(props: { entry: ToastEntry }) {
    const { entry } = props;
    const { t } = useTranslation();
    // Radix pauses the countdown while the viewport is hovered or focused, or the window is
    // in the background; the bar stops with it.
    const [paused, setPaused] = useState(false);

    const handleClick = (event: MouseEvent<HTMLLIElement>) => {
        // Radix cancels the click that ends a swipe, which closes the toast on its own.
        if (entry.closeOnClick && !event.defaultPrevented) closeToastEntry(entry.key);
    };

    return (
        <RadixToast.Root
            className={classNames(styles.toast, entry.closeOnClick && styles.closeOnClick)}
            data-kind={entry.kind}
            open={entry.open}
            onOpenChange={(open) => {
                if (!open) closeToastEntry(entry.key);
            }}
            duration={entry.autoClose === false ? Number.POSITIVE_INFINITY : entry.autoClose}
            onPause={() => setPaused(true)}
            onResume={() => setPaused(false)}
            onClick={handleClick}>
            <div className={styles.body}>
                <div className={styles.icon}>{entry.icon}</div>
                <RadixToast.Description className={styles.content}>
                    {entry.content}
                </RadixToast.Description>
            </div>
            <RadixToast.Close
                className={styles.close}
                aria-label={t('molecules.toastTemplate.close')}>
                <SvgIcon icon={CloseLine} size="custom" customSize={10} color="currentColor" />
            </RadixToast.Close>
            {entry.showProgress && entry.autoClose !== false && (
                <div className={styles.progress} aria-hidden="true">
                    <div
                        className={styles.progressBar}
                        data-paused={paused || undefined}
                        style={{ animationDuration: `${entry.autoClose}ms` }}
                    />
                </div>
            )}
        </RadixToast.Root>
    );
}
